// Complete API Service Layer for Meeting Management System
// WORLD-CLASS OPTIMIZATIONS: Request cancellation, retry logic, timeout optimization, debouncing
import axios from "axios";

// UAT LOCAL URL
export const baseUrl = "https://mrms.kanishkatech.com/api";

// PRODUCTION URL
// export const baseUrl = "https://mosil.org/api/";

// Local development URL
// export const baseUrl = "http://192.168.1.108:8001";
// export const baseUrl = 'http://localhost:8001'; 
// export const baseUrl = 'https://6ktnhm66-8001.inc1.devtunnels.ms/';

// ============================================================================
// REQUEST QUEUE & CONCURRENCY MANAGEMENT
// ============================================================================

// Global request queue to limit concurrent requests
const MAX_CONCURRENT_REQUESTS = 3;
let activeRequests = 0;
const requestQueue = [];

const processQueue = () => {
  if (activeRequests >= MAX_CONCURRENT_REQUESTS || requestQueue.length === 0) {
    return;
  }

  const { request, resolve, reject } = requestQueue.shift();
  activeRequests++;

  request()
    .then(resolve)
    .catch(reject)
    .finally(() => {
      activeRequests--;
      processQueue();
    });
};

const queueRequest = (requestFn) => {
  return new Promise((resolve, reject) => {
    requestQueue.push({ request: requestFn, resolve, reject });
    processQueue();
  });
};

// ============================================================================
// RETRY LOGIC WITH EXPONENTIAL BACKOFF
// ============================================================================

const retryRequest = async (requestFn, maxRetries = 3, baseDelay = 1000) => {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;

      // Don't retry on 4xx errors (client errors) except 429 (rate limit)
      if (
        error.response?.status >= 400 &&
        error.response?.status < 500 &&
        error.response?.status !== 429
      ) {
        throw error;
      }

      // Don't retry on cancellation
      if (axios.isCancel(error)) {
        throw error;
      }

      // Last attempt, throw error
      if (attempt === maxRetries) {
        throw error;
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
};

// ============================================================================
// DEBOUNCE UTILITY
// ============================================================================

export const debounce = (fn, delay = 300) => {
  let timerId = null;
  return (...args) => {
    clearTimeout(timerId);
    timerId = setTimeout(() => {
      timerId = null;
      fn(...args);
    }, delay);
  };
};

// ============================================================================
// SEQUENTIAL BATCH EXECUTION
// ============================================================================

export const executeSequentially = async (promiseFactories, batchSize = 2) => {
  const results = [];
  for (let i = 0; i < promiseFactories.length; i += batchSize) {
    const batch = promiseFactories.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map((fn) => fn()));
    results.push(...batchResults);
  }
  return results;
};

// Helper to convert Promise.all to sequential batches
export const executeInBatches = async (promiseFactories, batchSize = 2) => {
  return executeSequentially(promiseFactories, batchSize);
};

// ============================================================================
// AXIOS INSTANCE WITH OPTIMIZED CONFIG
// ============================================================================

// Create axios instance with optimized config
const apiClient = axios.create({
  baseURL: baseUrl,
  timeout: 30000, // Reduced to 30s for faster failure detection (was 120s)
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token and AbortController support
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("meettrack_user");
    if (token) {
      const userData = JSON.parse(token);
      config.headers.Authorization = `Bearer ${userData.token}`;
    }

    // Support AbortController if provided in options
    // Signal will be set by createRequest function

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling with retry logic
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Don't redirect to login if it's already a login/auth endpoint
      const isAuthEndpoint = error.config?.url?.includes("/auth/");

      if (!isAuthEndpoint) {
        // Token expired for protected routes, redirect to login
        localStorage.removeItem("meettrack_user");
        window.location.href = "/login";
      }
    }

    // NEW: Handle cases where error response is a Blob (common with responseType: 'blob')
    // We need to read the Blob asynchronously before extracting the error message
    if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
      try {
        const text = await error.response.data.text();
        error.response.data = JSON.parse(text);
      } catch (e) {
        console.error("Failed to parse error blob:", e);
      }
    }

    // Extract error message from response
    const errorMessage = extractErrorMessage(error);
    const enhancedError = {
      ...error,
      message: errorMessage,
      originalMessage: error.message,
    };

    return Promise.reject(enhancedError);
  },
);

// ============================================================================
// ENHANCED API CLIENT WITH RETRY & QUEUE
// ============================================================================

const createRequest = (requestFn, options = {}) => {
  const { retries = 3, timeout = 30000, priority = "normal", signal } = options;

  // Create request function that applies timeout and signal
  const wrappedRequest = () => {
    // Call original request function - it returns a promise
    // requestFn should already include signal in axios config
    const originalPromise = requestFn();

    // If signal is aborted, reject immediately
    if (signal?.aborted) {
      return Promise.reject(new Error("Request aborted"));
    }

    // Apply custom timeout if different from default
    let finalPromise = originalPromise;
    if (timeout !== 30000) {
      const timeoutPromise = new Promise((_, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error("Request timeout"));
        }, timeout);

        // Clear timeout if request completes
        originalPromise.finally(() => clearTimeout(timeoutId));
      });
      finalPromise = Promise.race([originalPromise, timeoutPromise]);
    }

    return finalPromise;
  };

  // Queue request for concurrency control
  const queuedRequest = () => queueRequest(wrappedRequest);

  // Add retry logic
  return retryRequest(queuedRequest, retries);
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Helper function to extract user-friendly error messages (exported for use in other files)
export function extractErrorMessage(error) {
  // Network errors (no response from server)
  if (!error.response) {
    if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
      return "Request timed out. Please check your connection and try again.";
    }
    if (axios.isCancel(error)) {
      return "Request was cancelled.";
    }
    return "Network error. Please check your internet connection.";
  }

  const status = error.response.status;
  const data = error.response.data;

  // Extract specific error message from response
  if (data?.detail) {
    return Array.isArray(data.detail)
      ? data.detail.map((d) => d.msg || d).join(", ")
      : data.detail;
  }

  if (data?.message) {
    return data.message;
  }

  // Generic error messages by status code
  const statusMessages = {
    400: "Invalid request. Please check your input.",
    401: "Authentication required. Please login again.",
    403: "You do not have permission to perform this action.",
    404: "Resource not found.",
    409: "Conflict detected. This resource already exists.",
    422: "Validation error. Please check your input.",
    429: "Too many requests. Please try again later.",
    500: "Internal server error. Please try again later.",
    502: "Bad gateway. Server is temporarily unavailable.",
    503: "Service temporarily unavailable. Please try again later.",
  };

  return statusMessages[status] || `Error ${status}: Something went wrong.`;
}

// Helper function to handle API response with success/error messages
export function handleAPIResponse(response, successMessage) {
  if (response?.data) {
    return {
      success: true,
      data: response.data,
      message: successMessage,
    };
  }
  return { success: false, data: null, message: successMessage };
}

// Function to set authorization token
export const setAuthToken = (token) => {
  if (token) {
    apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common["Authorization"];
  }
};

// ============================================================================
// API FUNCTIONS WITH OPTIMIZED CONFIG
// ============================================================================

// Authentication APIs
export const authAPI = {
  sendOTP: (email, options = {}) =>
    createRequest(() => apiClient.post("/auth/send-otp", { email }), {
      retries: 0,
      ...options,
    }),
  resendOTP: (email, options = {}) =>
    createRequest(() => apiClient.post("/auth/resend-otp", email), {
      retries: 0,
      ...options,
    }),
  login: (email, code, options = {}) =>
    createRequest(() => apiClient.post("/auth/login", { email, code }), {
      retries: 0,
      ...options,
    }),
  adminLogin: (email, password, options = {}) =>
    createRequest(
      () => apiClient.post("/auth/admin-login", { email, password }),
      { retries: 0, ...options },
    ),
  getSession: (options = {}) =>
    createRequest(() => apiClient.get("/auth/session"), {
      retries: 2, 
      ...options,
    }),
};

// User APIs
export const userAPI = {
  getMe: (options = {}) => {
    const { signal, ...restOptions } = options;
    return createRequest(() => apiClient.get("/users/me", { signal }), {
      retries: 2,
      ...restOptions,
    });
  },
  getUsers: (params = {}, options = {}) => {
    const { signal, ...restOptions } = options;
    return createRequest(() => apiClient.get("/users/", { params, signal }), {
      retries: 2,
      ...restOptions,
    });
  },
  getFrequentParticipants: (options = {}) =>
    createRequest(() => apiClient.get("/users/frequent-participants"), {
      retries: 2,
      ...options,
    }),
  getOrganizers: (options = {}) =>
    createRequest(() => apiClient.get("/users/organizers"), {
      retries: 2,
      ...options,
    }),
  createUser: (userData, options = {}) =>
    createRequest(() => apiClient.post("/users/", userData), {
      retries: 1,
      ...options,
    }),
  updateUser: (userId, userData, options = {}) =>
    createRequest(() => apiClient.put(`/users/${userId}`, userData), {
      retries: 1,
      ...options,
    }),
  deleteUser: (userId, options = {}) =>
    createRequest(() => apiClient.delete(`/users/${userId}`), {
      retries: 1,
      ...options,
    }),
  toggleUserActive: (userId, options = {}) =>
    createRequest(() => apiClient.patch(`/users/${userId}/toggle-active`), {
      retries: 2,
      ...options,
    }),
  getUser: (userId, options = {}) =>
    createRequest(() => apiClient.get(`/users/${userId}`), {
      retries: 2,
      ...options,
    }),
  getUserStats: (params = {}, options = {}) =>
    createRequest(() => apiClient.get("/users/stats", { params }), {
      retries: 2,
      ...options,
    }),

  exportmeeting: (params = {}, options = {}) => {
    const { retries = 2, ...apiOptions } = options;
    return createRequest(
      () =>
        apiClient.get("/reports/meetings/export", {
          params,
          responseType: "blob",
          ...apiOptions,
        }),
      { retries, ...apiOptions },
    );
  },

  exporttask : (params = {}, options = {}) => {
    const { retries = 2, ...apiOptions } = options;
    return createRequest(
      () =>
        apiClient.get("/reports/action-points/export", {
          params,
          responseType: "blob",
          ...apiOptions,
        }),
      { retries, ...apiOptions },
    );
  },

  validateBulkUploadFile: (file, options = {}) => {
    const formData = new FormData();
    formData.append("file", file);
    return createRequest(
      () =>
        apiClient.post("/users/bulk-upload/validate", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        }),
      { retries: 1, timeout: 60000, ...options }, // 60s for file uploads
    );
  },
  bulkUploadUsers: (file, options = {}) => {
    const formData = new FormData();
    formData.append("file", file);
    return createRequest(
      () =>
        apiClient.post("/users/bulk-upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        }),
      { retries: 1, timeout: 60000, ...options }, // 60s for file uploads
    );
  },
};

// Meeting APIs
export const meetingAPI = {
  getMeetings: (params = {}, options = {}) =>
    createRequest(() => apiClient.get("/meetings/", { params }), {
      retries: 2,
      ...options,
    }),
  createMeeting: (meetingData, skipNotification = false, options = {}) => {
    if (meetingData instanceof FormData) {
      return createRequest(
        () =>
          apiClient.post("/meetings/", meetingData, {
            params: { skip_notification: skipNotification },
            headers: { "Content-Type": undefined },
          }),
        { retries: 1, timeout: 60000, ...options }, // 60s for file uploads
      );
    }
    return createRequest(
      () =>
        apiClient.post("/meetings/", meetingData, {
          params: { skip_notification: skipNotification },
        }),
      { retries: 1, ...options },
    );
  },
  getMeeting: (meetingId, options = {}) =>
    createRequest(() => apiClient.get(`/meetings/${meetingId}`), {
      retries: 2,
      ...options,
    }),
  updateMeeting: (
    meetingId,
    meetingData,
    skipNotification = false,
    options = {},
  ) => {
    // Extract update_scope to send as query param; keep the rest in the body
    const { update_scope, ...bodyData } = meetingData instanceof FormData
      ? { update_scope: undefined }
      : meetingData;

    if (meetingData instanceof FormData) {
      return createRequest(
        () =>
          apiClient.put(`/meetings/${meetingId}`, meetingData, {
            params: {
              skip_notification: skipNotification,
              ...(update_scope && { update_scope }),
            },
            headers: { "Content-Type": undefined },
          }),
        { retries: 1, timeout: 60000, ...options }, // 60s for file uploads
      );
    }
    return createRequest(
      () =>
        apiClient.put(`/meetings/${meetingId}`, bodyData, {
          params: {
            skip_notification: skipNotification,
            ...(update_scope && { update_scope }),
          },
        }),
      { retries: 1, ...options },
    );
  },
  deleteMeeting: (meetingId, options = {}) =>
    createRequest(() => apiClient.delete(`/meetings/${meetingId}`), {
      retries: 1,
      ...options,
    }),
  cancelMeeting: (meetingId, cancelOptions = {}, apiOptions = {}) =>
    createRequest(
      () => apiClient.post(`/meetings/${meetingId}/cancel`, cancelOptions),
      { retries: 1, ...apiOptions },
    ),
  completeMeeting: (meetingId, options = {}) =>
    createRequest(() => apiClient.post(`/meetings/${meetingId}/complete`), {
      retries: 1,
      ...options,
    }),
  getMeetingStats: (options = {}) =>
    createRequest(() => apiClient.get("/meetings/stats"), {
      retries: 2,
      ...options,
    }),
  getCalendarEvents: (params = {}, options = {}) =>
    createRequest(() => apiClient.get("/meetings/calendar", { params }), {
      retries: 2,
      ...options,
    }),
  addMeetingNote: (meetingId, noteData, options = {}) =>
    createRequest(
      () => apiClient.post(`/meetings/${meetingId}/notes`, noteData),
      { retries: 1, ...options },
    ),

  // Agenda Documents
  uploadAgendaDocument: (meetingId, file, options = {}) => {
    const formData = new FormData();
    formData.append("file", file);
    return createRequest(
      () =>
        apiClient.post(`/meetings/${meetingId}/agenda-documents`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        }),
      { retries: 1, timeout: 60000, ...options }, // 60s for file uploads
    );
  },
  getAgendaDocuments: (meetingId, options = {}) =>
    createRequest(
      () => apiClient.get(`/meetings/${meetingId}/agenda-documents`),
      { retries: 2, ...options },
    ),
  deleteAgendaDocument: (meetingId, documentIndex, options = {}) =>
    createRequest(
      () =>
        apiClient.delete(
          `/meetings/${meetingId}/agenda-documents/${documentIndex}`,
        ),
      { retries: 1, ...options },
    ),

  // Send notification
  sendNotification: (meetingId, isUpdate = false, options = {}) =>
    createRequest(
      () =>
        apiClient.post(`/meetings/${meetingId}/send-notification`, null, {
          params: { is_update: isUpdate },
        }),
      { retries: 1, ...options },
    ),
};

// Action Point APIs
export const actionPointAPI = {
  getActionPointsForMeeting: (meetingId, params = {}, options = {}) =>
    createRequest(
      () => apiClient.get(`/action-points/meetings/${meetingId}`, { params }),
      { retries: 2, ...options },
    ),
  createActionPoints: (meetingId, actionPoints, options = {}) =>
    createRequest(
      () =>
        apiClient.post(`/action-points/meetings/${meetingId}`, actionPoints),
      { retries: 1, ...options },
    ),
  getActionPoint: (apId, options = {}) =>
    createRequest(() => apiClient.get(`/action-points/${apId}`), {
      retries: 2,
      ...options,
    }),
  deleteActionPoint: (apId, options = {}) =>
    createRequest(() => apiClient.delete(`/action-points/${apId}`), {
      retries: 1,
      ...options,
    }),

  // Assignee APIs
  getMyActionPoints: (params = {}, options = {}) =>
    createRequest(() => apiClient.get("/action-points/my", { params }), {
      retries: 2,
      ...options,
    }),

  // Assigned By Me APIs
  getAssignedByMeTabs: (options = {}) =>
    createRequest(() => apiClient.get("/action-points/assigned-by-me/tabs"), {
      retries: 2,
      ...options,
    }),

  getAssignedByMeFilters: (options = {}) =>
    createRequest(() => apiClient.get("/action-points/assigned-by-me/filters"), {
      retries: 2,
      ...options,
    }),

  getAssignedByMe: (params = {}, options = {}) =>
    createRequest(() => apiClient.get("/action-points/assigned-by-me", { params }), {
      retries: 2,
      ...options,
    }),

  exportAssignedByMe: (params = {}, options = {}) => {
    const { retries = 2, ...apiOptions } = options;
    return createRequest(
      () =>
        apiClient.get("/action-points/assigned-by-me/export", {
          params,
          responseType: "blob",
          ...apiOptions,
        }),
      { retries, ...apiOptions },
    );
  },

  // Admin APIs
  getAllActionPointsAdmin: (params = {}, options = {}) =>
    createRequest(() => apiClient.get("/action-points/admin/all", { params }), {
      retries: 2,
      ...options,
    }),

  exportAllActionPointsAdmin: (params = {}, options = {}) => {
    const { retries = 2, ...apiOptions } = options;
    return createRequest(
      () =>
        apiClient.get("/action-points/admin/all/export", {
          params,
          responseType: "blob",
          ...apiOptions,
        }),
      { retries, ...apiOptions },
    );
  },

  // Approver APIs
  getReviewItems: (params = {}, options = {}) =>
    createRequest(
      () => apiClient.get("/action-points/approver/review", { params }),
      { retries: 2, ...options },
    ),
  uploadDocument: (apId, file, options = {}) => {
    const formData = new FormData();
    formData.append("file", file);
    return createRequest(
      () =>
        apiClient.post(`/action-points/${apId}/assignee/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        }),
      { retries: 1, timeout: 60000, ...options }, // 60s for file uploads
    );
  },
  submitForReview: (apId, comment, options = {}) =>
    createRequest(
      () =>
        apiClient.post(`/action-points/${apId}/assignee/submit`, { comment }),
      { retries: 1, ...options },
    ),
  resetSubmission: (apId, options = {}) =>
    createRequest(
      () => apiClient.post(`/action-points/${apId}/assignee/reset`),
      { retries: 1, ...options },
    ),

  // Review APIs
  approveReview: (apId, options = {}) =>
    createRequest(
      () => apiClient.post(`/action-points/${apId}/review/approve`),
      { retries: 1, ...options },
    ),
  rejectReview: (apId, comment, options = {}) =>
    createRequest(
      () => apiClient.post(`/action-points/${apId}/review/reject`, { comment }),
      { retries: 1, ...options },
    ),

  // Get review count for employee review (approver/review endpoint)
  getEmployeeReviewCount: (options = {}) =>
    createRequest(
      () =>
        apiClient.get("/action-points/approver/review", {
          params: { page: 1, page_size: 1 },
        }),
      { retries: 2, ...options },
    ),

  // Get review count for business head (same endpoint, different role context)
  getBusinessHeadReviewCount: (options = {}) =>
    createRequest(
      () =>
        apiClient.get("/action-points/approver/review", {
          params: { page: 1, page_size: 1 },
        }),
      { retries: 2, ...options },
    ),
  validateBulkActionPoints: (file, options = {}) => {
    const formData = new FormData();
    formData.append("file", file);
    return createRequest(
      () =>
        apiClient.post("/action-points/bulk-upload/validate", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        }),
      { retries: 1, timeout: 60000, ...options }, // 60s for file uploads
    );
  },
};

// Report APIs
export const reportAPI = {
  getDashboard: (options = {}) =>
    createRequest(() => apiClient.get("/reports/dashboard"), {
      retries: 2,
      ...options,
    }),
  getOrgSummary: (options = {}) =>
    createRequest(() => apiClient.get("/reports/org-summary"), {
      retries: 2,
      ...options,
    }),
  getActionPointPerformance: (options = {}) =>
    createRequest(() => apiClient.get("/reports/action-point-performance"), {
      retries: 2,
      ...options,
    }),
  exportMeetingMOM: (meetingId, options = {}) =>
    createRequest(
      () =>
        apiClient.get(`/reports/meetings/${meetingId}/export`, {
          responseType: "blob",
          ...options,
        }),
      { retries: 1, ...options },
    ),
  exportActionableSummary: (params = {}, options = {}) => {
    const { retries = 2, ...apiOptions } = options;
    return createRequest(
      () =>
        apiClient.get("/reports/admin/actionable-summary/export", {
          params,
          responseType: "blob",
          ...apiOptions,
        }),
      { retries, ...apiOptions },
    );
  },
};

// Admin APIs
export const adminAPI = {
  getAdminUsers: (params = {}, options = {}) =>
    createRequest(() => apiClient.get("/admin/users", { params }), {
      retries: 2,
      ...options,
    }),
  getAdminMeetings: (params = {}, options = {}) =>
    createRequest(() => apiClient.get("/admin/meetings", { params }), {
      retries: 2,
      ...options,
    }),
  runDailyJobs: (options = {}) =>
    createRequest(() => apiClient.post("/admin/jobs/daily"), {
      retries: 1,
      ...options,
    }),
};

// Export all APIs
export default {
  auth: authAPI,
  users: userAPI,
  meetings: meetingAPI,
  actionPoints: actionPointAPI,
  reports: reportAPI,
  admin: adminAPI,
};
